import type { UploadPolicyMode } from '@obras/shared';
import {
  BRAZIL_TIMEZONES,
  DEFAULT_UPLOAD_EXTENSIONS,
  FILE_KIND_META,
  formatBytes,
  formatDateTime,
  groupExtensionsByKind,
  MAX_UPLOAD_LIMIT_BYTES,
  MAX_VISIT_INTERVAL_DAYS,
  MIN_UPLOAD_LIMIT_BYTES,
  MIN_VISIT_INTERVAL_DAYS,
  timezoneLabel,
  todayIn,
} from '@obras/shared';
import { CalendarClock, Check, Clock, Info, Paperclip, Plus, X } from 'lucide-react';
import { useId, useState, type FormEvent } from 'react';

import { PageHeader } from '../components/AppShell.js';
import { Button } from '../components/ui/Button.js';
import { FormSection } from '../components/ui/Card.js';
import { CONTROL_BASE, CONTROL_TONE, SelectField, TextField } from '../components/ui/Field.js';
import { ErrorState, FormError, Notice, Skeleton } from '../components/ui/Feedback.js';
import { useSettings, useUpdateSettings } from '../features/configuracoes/api.js';
import { ApiError } from '../lib/api.js';
import { useAuth } from '../lib/auth.js';

const ICON = { strokeWidth: 1.5 } as const;

/**
 * Uma lista de extensões que se edita item a item.
 *
 * Um campo de texto livre com vírgulas seria mais curto de escrever e pior de
 * usar: ninguém enxerga o que já está na lista, e um espaço a mais vira uma
 * extensão fantasma.
 *
 * O botão "Acrescentar" existe além do Enter porque atalho invisível não é
 * afordância — quem nunca usou a tela não descobre que basta apertar Enter.
 */
function ListaDeExtensoes({
  value,
  onChange,
  titulo,
  hint,
  placeholder,
}: {
  value: readonly string[];
  onChange: (proximo: string[]) => void;
  titulo: string;
  hint: string;
  placeholder: string;
}) {
  const [digitado, setDigitado] = useState('');
  const campoId = useId();

  const limpo = digitado
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
  const podeAcrescentar = limpo !== '' && !value.includes(limpo);

  function acrescentar(): void {
    if (!podeAcrescentar) {
      setDigitado('');
      return;
    }
    onChange([...value, limpo]);
    setDigitado('');
  }

  return (
    <div>
      <label htmlFor={campoId} className="block text-[13px] font-medium">
        {titulo}
      </label>
      <p className="mt-1 text-[13px] text-ink-3">{hint}</p>

      <div className="mt-3 flex gap-2 sm:max-w-sm">
        <input
          id={campoId}
          value={digitado}
          onChange={(event) => setDigitado(event.target.value)}
          onKeyDown={(event) => {
            if (event.key !== 'Enter') return;
            // Sem isto o Enter enviaria o formulário inteiro da página.
            event.preventDefault();
            acrescentar();
          }}
          placeholder={placeholder}
          className={`${CONTROL_BASE} ${CONTROL_TONE.rest} h-10 min-w-0 flex-1 font-mono text-sm placeholder:font-sans`}
        />
        <Button
          variant="secondary"
          disabled={!podeAcrescentar}
          icon={<Plus {...ICON} size={16} />}
          onClick={acrescentar}
        >
          Adicionar
        </Button>
      </div>

      <div className="mt-3 flex flex-wrap gap-1.5">
        {value.length === 0 ? (
          <span className="text-[13px] text-ink-3">Nenhuma.</span>
        ) : (
          value.map((extensao) => (
            <span
              key={extensao}
              className="inline-flex items-center gap-1 rounded-md bg-sunken py-1 pr-1 pl-2.5 font-mono text-[12px]"
            >
              .{extensao}
              <button
                type="button"
                onClick={() => onChange(value.filter((item) => item !== extensao))}
                aria-label={`Tirar .${extensao} da lista`}
                className="grid size-5 place-items-center rounded-sm text-ink-3 transition-colors hover:bg-sunken-2 hover:text-ink"
              >
                <X size={12} strokeWidth={2} aria-hidden />
              </button>
            </span>
          ))
        )}
      </div>
    </div>
  );
}

export function ConfiguracoesPage() {
  const { organization } = useAuth();
  const { data, isPending, isError, error, refetch } = useSettings();
  const update = useUpdateSettings();

  const [interval, setInterval] = useState('');
  const [timezone, setTimezone] = useState('');
  const [uploadPolicy, setUploadPolicy] = useState<UploadPolicyMode>('padrao');
  const [extras, setExtras] = useState<string[]>([]);
  const [blocked, setBlocked] = useState<string[]>([]);
  const [limiteMb, setLimiteMb] = useState('');
  const [pronto, setPronto] = useState(false);
  const [salvo, setSalvo] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  /*
   * Carrega os valores na primeira renderização com dados.
   *
   * Este `if` agenda uma nova renderização, mas a atual segue até o fim — e é
   * por isso que o formulário só aparece com `pronto`. Sem essa espera, o
   * primeiro quadro desenhava o relógio com o fuso ainda vazio.
   */
  if (data && !pronto) {
    setInterval(String(data.defaultVisitIntervalDays));
    setTimezone(data.timezone);
    setUploadPolicy(data.uploadPolicy);
    setExtras([...data.uploadExtras]);
    setBlocked([...data.uploadBlocked]);
    setLimiteMb(String(Math.round(data.maxUploadBytes / (1024 * 1024))));
    setPronto(true);
  }

  const mesmaLista = (a: readonly string[], b: readonly string[]): boolean =>
    a.length === b.length && a.every((item, indice) => item === b[indice]);

  const mudou =
    data !== undefined &&
    (Number(interval) !== data.defaultVisitIntervalDays ||
      timezone !== data.timezone ||
      uploadPolicy !== data.uploadPolicy ||
      !mesmaLista(extras, data.uploadExtras) ||
      !mesmaLista(blocked, data.uploadBlocked) ||
      Number(limiteMb) * 1024 * 1024 !== data.maxUploadBytes);

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setFormError(null);
    setFieldErrors({});
    setSalvo(false);

    const dias = Number(interval);
    if (
      !Number.isInteger(dias) ||
      dias < MIN_VISIT_INTERVAL_DAYS ||
      dias > MAX_VISIT_INTERVAL_DAYS
    ) {
      setFieldErrors({
        defaultVisitIntervalDays: `Informe um número entre ${MIN_VISIT_INTERVAL_DAYS} e ${MAX_VISIT_INTERVAL_DAYS}.`,
      });
      return;
    }

    const bytes = Number(limiteMb) * 1024 * 1024;
    if (
      !Number.isFinite(bytes) ||
      bytes < MIN_UPLOAD_LIMIT_BYTES ||
      bytes > MAX_UPLOAD_LIMIT_BYTES
    ) {
      setFieldErrors({
        maxUploadBytes: `Informe um número entre ${MIN_UPLOAD_LIMIT_BYTES / (1024 * 1024)} e ${MAX_UPLOAD_LIMIT_BYTES / (1024 * 1024)}.`,
      });
      return;
    }

    try {
      await update.mutateAsync({
        defaultVisitIntervalDays: dias,
        timezone,
        uploadPolicy,
        uploadExtras: extras,
        uploadBlocked: blocked,
        maxUploadBytes: bytes,
      });
      setPronto(false);
      setSalvo(true);
    } catch (erro) {
      if (erro instanceof ApiError) {
        setFieldErrors(erro.fieldErrors());
        setFormError(erro.details ? 'Verifique os campos destacados.' : erro.message);
      } else {
        setFormError('Não foi possível salvar as configurações.');
      }
    }
  }

  return (
    <>
      <PageHeader
        title="Configurações"
        description={`Como o sistema se comporta em ${organization?.name ?? 'seu órgão'}.`}
      />

      {isError ? (
        <ErrorState
          message={error instanceof Error ? error.message : 'Erro desconhecido.'}
          onRetry={() => void refetch()}
        />
      ) : isPending || !pronto || !data ? (
        <div className="flex flex-col gap-4">
          <Skeleton className="h-52 rounded-lg" />
          <Skeleton className="h-64 rounded-lg" />
        </div>
      ) : (
        <form
          onSubmit={(event) => void handleSubmit(event)}
          className="flex flex-col gap-4"
          noValidate
        >
          <FormError>{formError}</FormError>

          {salvo && !mudou ? (
            <Notice icon={Check}>
              Configurações salvas. Os prazos de visita já foram recalculados com o novo valor.
            </Notice>
          ) : null}

          <FormSection
            icon={CalendarClock}
            title="Intervalo entre visitas"
            description="De quantos em quantos dias uma obra precisa ser visitada. É o prazo que decide se ela aparece como em dia, vencendo ou vencida."
          >
            <div className="flex flex-col gap-4 sm:max-w-xs">
              <TextField
                label="Dias"
                type="number"
                inputMode="numeric"
                min={MIN_VISIT_INTERVAL_DAYS}
                max={MAX_VISIT_INTERVAL_DAYS}
                value={interval}
                onChange={(event) => setInterval(event.target.value)}
                hint={`Entre ${MIN_VISIT_INTERVAL_DAYS} e ${MAX_VISIT_INTERVAL_DAYS} dias.`}
                {...(fieldErrors['defaultVisitIntervalDays']
                  ? { error: fieldErrors['defaultVisitIntervalDays'] }
                  : {})}
              />
            </div>

            {data.obrasWithOwnInterval > 0 ? (
              <p className="mt-4 flex items-start gap-2 text-[13px] text-ink-3">
                <Info {...ICON} size={15} className="mt-0.5 shrink-0" aria-hidden />
                <span>
                  {data.obrasWithOwnInterval === 1
                    ? 'Uma obra tem intervalo próprio e não segue este padrão.'
                    : `${data.obrasWithOwnInterval} obras têm intervalo próprio e não seguem este padrão.`}{' '}
                  O padrão vale para as demais.
                </span>
              </p>
            ) : null}
          </FormSection>

          <FormSection
            icon={Clock}
            title="Fuso horário"
            description="Fuso de referência do órgão. Determina a data considerada corrente e, por consequência, o momento em que uma visita passa a constar como vencida."
          >
            <div className="flex flex-col gap-4 sm:max-w-md">
              <SelectField
                label="Horário oficial"
                value={timezone}
                onChange={(event) => setTimezone(event.target.value)}
              >
                {BRAZIL_TIMEZONES.map((zona) => (
                  <option key={zona.id} value={zona.id}>
                    {zona.label}
                  </option>
                ))}
              </SelectField>

              <p className="text-[13px] text-ink-3">
                {BRAZIL_TIMEZONES.find((zona) => zona.id === timezone)?.where}
              </p>
            </div>

            {/*
              O relógio acompanha a escolha antes de salvar, e é ele que dá
              sentido ao seletor: "America/Cuiaba" não diz nada a quem só quer
              saber se o sistema está na hora daqui. As duas linhas usam a
              mesma função que o servidor usa, então não têm como discordar.
            */}
            <dl className="mt-5 flex flex-col gap-2 rounded-lg bg-sunken p-4 text-[13px] sm:max-w-md">
              <div className="flex items-baseline justify-between gap-4">
                <dt className="text-ink-3">Agora, em {timezoneLabel(timezone)}</dt>
                <dd className="tabular font-mono text-[12px]">
                  {formatDateTime(new Date().toISOString(), timezone)}
                </dd>
              </div>
              <div className="flex items-baseline justify-between gap-4">
                <dt className="text-ink-3">A data de hoje</dt>
                <dd className="tabular font-mono text-[12px]">{todayIn(timezone)}</dd>
              </div>
            </dl>
          </FormSection>

          <FormSection
            icon={Paperclip}
            title="Arquivos aceitos"
            description="O que a equipe pode anexar ao dossiê das obras, e até que tamanho. Vale para toda a organização."
          >
            <div className="flex flex-col gap-6">
              <div className="flex flex-col gap-4 sm:max-w-md">
                <SelectField
                  label="Como a lista funciona"
                  value={uploadPolicy}
                  onChange={(event) => setUploadPolicy(event.target.value as UploadPolicyMode)}
                >
                  <option value="padrao">Lista padrão, com ajustes</option>
                  <option value="lista_branca">Somente o que eu listar</option>
                </SelectField>

                <p className="text-[13px] text-ink-3">
                  {uploadPolicy === 'padrao'
                    ? `Parte dos ${DEFAULT_UPLOAD_EXTENSIONS.length} formatos de fábrica. Você acrescenta o que faltar e bloqueia o que não quiser.`
                    : 'A lista de fábrica é ignorada por completo: passa apenas o que estiver escrito abaixo.'}
                </p>
              </div>

              <ListaDeExtensoes
                titulo={uploadPolicy === 'padrao' ? 'Acrescentar' : 'Formatos aceitos'}
                hint={
                  uploadPolicy === 'padrao'
                    ? 'Formatos além dos de fábrica.'
                    : 'Somente estes serão aceitos. A lista de fábrica é ignorada.'
                }
                placeholder="dwg"
                value={extras}
                onChange={setExtras}
              />

              {uploadPolicy === 'padrao' ? (
                <ListaDeExtensoes
                  titulo="Bloquear"
                  hint="Formatos de fábrica que este órgão não quer receber."
                  placeholder="rar"
                  value={blocked}
                  onChange={setBlocked}
                />
              ) : null}

              <div className="flex flex-col gap-4 sm:max-w-xs">
                <TextField
                  label="Tamanho máximo por arquivo (MB)"
                  type="number"
                  inputMode="numeric"
                  min={MIN_UPLOAD_LIMIT_BYTES / (1024 * 1024)}
                  max={MAX_UPLOAD_LIMIT_BYTES / (1024 * 1024)}
                  value={limiteMb}
                  onChange={(event) => setLimiteMb(event.target.value)}
                  hint={`Até ${formatBytes(MAX_UPLOAD_LIMIT_BYTES)}, que é o teto do servidor.`}
                  {...(fieldErrors['maxUploadBytes']
                    ? { error: fieldErrors['maxUploadBytes'] }
                    : {})}
                />
              </div>

              {/*
                O que vale hoje, com a conta já feita. Mostrar modo, acréscimos
                e bloqueios sem o resultado obrigaria o gestor a somar de cabeça
                para responder "o pessoal consegue enviar .rvt?".
              */}
              <div className="rounded-lg bg-sunken p-4">
                <p className="text-[13px] font-medium">
                  Aceitos hoje: {data.allowedExtensions.length} formatos
                </p>
                <p className="mt-1 text-[13px] text-ink-3">
                  A regra vale a partir do que está salvo. Imagens são sempre aceitas, por serem
                  requisito da galeria da obra.
                </p>

                <dl className="mt-4 flex flex-col gap-3">
                  {groupExtensionsByKind(data.allowedExtensions).map((grupo) => (
                    <div key={grupo.kind}>
                      <dt className="text-[12px] text-ink-3">{FILE_KIND_META[grupo.kind].label}</dt>
                      <dd className="mt-1 font-mono text-[12px] break-words">
                        {grupo.extensions.map((extensao) => `.${extensao}`).join('  ')}
                      </dd>
                    </div>
                  ))}
                </dl>
              </div>
            </div>
          </FormSection>

          <div className="flex items-center justify-end gap-3">
            {mudou ? (
              <span className="text-[13px] text-ink-3">Há alterações não salvas.</span>
            ) : null}
            <Button
              type="submit"
              variant="primary"
              disabled={!mudou}
              loading={update.isPending}
              icon={<Check {...ICON} size={16} />}
            >
              Salvar
            </Button>
          </div>
        </form>
      )}
    </>
  );
}
